import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FiActivity, FiBell, FiTrendingUp } from 'react-icons/fi';
import { LanguageMenu } from '@web/app/shell/LanguageMenu';
import classes from './AuthLayout.module.css';

/** Split-panel layout shared by every signed-out screen (login, reset, invitation, setup). */
export function AuthLayout({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  const highlights = [
    { icon: <FiActivity size={15} />, text: t('auth.brand.highlight1') },
    { icon: <FiBell size={15} />, text: t('auth.brand.highlight2') },
    { icon: <FiTrendingUp size={15} />, text: t('auth.brand.highlight3') },
  ];

  return (
    <div className={classes.page}>
      <section className={classes.brand} aria-hidden="false">
        <div className={classes.brandLogo}>
          <img src="/brand/twinlink-logo.png" alt="" />
          <span className={classes.brandLogoWord}>TwinLink</span>
        </div>
        <div className={classes.brandContent}>
          <p className={classes.eyebrow}>{t('auth.brand.eyebrow')}</p>
          <p className={classes.headline}>{t('auth.brand.headline')}</p>
          <p className={classes.tagline}>{t('auth.brand.tagline')}</p>
          <ul className={classes.highlights}>
            {highlights.map((h) => (
              <li key={h.text} className={classes.highlight}>
                <span className={classes.highlightIcon} aria-hidden>
                  {h.icon}
                </span>
                <span>{h.text}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className={classes.brandFooter}>
          <div>
            <img src="/brand/smardac-logo-light.png" alt="SmarDac" className={classes.smardacLogo} />
            <p style={{ marginTop: 10 }}>{t('auth.brand.footer', { year })}</p>
          </div>
          <p className={classes.brandFooterLinks}>
            <a href="https://smardac.com" target="_blank" rel="noreferrer">
              SMARDAC
            </a>
            <span aria-hidden>·</span>
            <a href="https://hub.smardac.com" target="_blank" rel="noreferrer">
              SmarDac Hub
            </a>
          </p>
        </div>
      </section>

      <main className={classes.panel}>
        <div className={classes.langSwitch}>
          <LanguageMenu />
        </div>
        <div className={classes.card}>
          <div className={classes.mobileLogo}>
            <img src="/brand/twinlink-logo.png" alt="" />
            <span>TwinLink</span>
          </div>
          <h1 className={classes.cardTitle}>{title}</h1>
          {subtitle && <p className={classes.cardSubtitle}>{subtitle}</p>}
          {children}
        </div>
        <div className={classes.panelFooter}>
          <img src="/brand/smardac-logo-dark.png" alt="SmarDac" />
          <span>{t('auth.brand.footer', { year })}</span>
        </div>
      </main>
    </div>
  );
}
